import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BadgeListInput } from "@/components/ui/BadgeListInput";
import { cn } from "@/lib/utils";
import { AutoFillBanner } from "@/components/onboarding/formular-notificare/AutoFillBanner";
import { FieldSelectionDialog } from "@/components/onboarding/formular-notificare/FieldSelectionDialog";
import { FieldTooltip } from "@/components/onboarding/shared/FieldTooltip";
import { ONBOARDING_TOOLTIPS } from "@/utils/onboardingTooltips";
import type { EntityData } from "@/types/entity";
import type { CuiDuplicateMatch } from "@/utils/multi-session";
import type {
  FieldDiff,
  FetchCompanyDataResultStatus,
} from "@/hooks/useOnboardingForm";

type CompanyIdentificationSectionProps = {
  entityData: Partial<EntityData>;
  onFieldChange: (field: keyof EntityData, value: unknown) => void;
  fieldErrors: Record<string, string>;
  onFetchCompanyData: () => void;
  isFetchingCompanyData: boolean;
  fetchCompanyDataResult: FetchCompanyDataResultStatus;
  autoFilledFields: Set<string>;
  fieldDiffs: FieldDiff[];
  showFieldSelectionDialog: boolean;
  onFieldSelectionDialogChange: (open: boolean) => void;
  onFieldSelectionConfirm: (selectedFields: string[]) => void;
  duplicateCuiMatch?: CuiDuplicateMatch | null;
};

function FieldError({
  fieldName,
  errors,
}: {
  fieldName: string;
  errors: Record<string, string>;
}) {
  const error = errors[fieldName];
  if (!error) return null;

  return (
    <div className="flex items-center gap-2 mt-1">
      <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
      <span className="text-xs text-destructive">{error}</span>
    </div>
  );
}

function CompanyIdentificationSection({
  entityData,
  onFieldChange,
  fieldErrors,
  onFetchCompanyData,
  isFetchingCompanyData,
  fetchCompanyDataResult,
  autoFilledFields,
  fieldDiffs,
  showFieldSelectionDialog,
  onFieldSelectionDialogChange,
  onFieldSelectionConfirm,
  duplicateCuiMatch,
}: CompanyIdentificationSectionProps) {
  const fieldHighlight = (field: string) =>
    autoFilledFields.has(field)
      ? "ring-2 ring-ds-blue-300 ring-offset-1 bg-ds-blue-50/50 transition-all duration-500"
      : "transition-all duration-500";

  return (
    <div className="space-y-4">
      <p className="text-xs text-ds-text-400">
        Câmpurile marcate cu{" "}
        <span className="text-destructive font-medium">*</span> sunt obligatorii
      </p>

      <AutoFillBanner
        cui={entityData.cui || ""}
        variant="company"
        isFetching={isFetchingCompanyData}
        fetchResult={fetchCompanyDataResult}
        onFetch={onFetchCompanyData}
      />

      <FieldSelectionDialog
        open={showFieldSelectionDialog}
        onOpenChange={onFieldSelectionDialogChange}
        diffs={fieldDiffs}
        onConfirm={onFieldSelectionConfirm}
      />

      <div>
        <Label
          htmlFor="nume-entitate"
          className="inline-flex items-center gap-1"
        >
          Denumire entitate <span className="text-destructive ml-1">*</span>
          <FieldTooltip content={ONBOARDING_TOOLTIPS.nume_entitate} />
        </Label>
        <Input
          id="nume-entitate"
          value={entityData.nume_entitate || ""}
          onChange={(e) => onFieldChange("nume_entitate", e.target.value)}
          placeholder="Denumirea completă a companiei"
          className={cn(
            "text-xs md:text-sm",
            fieldHighlight("nume_entitate"),
            fieldErrors["nume_entitate"] && "border-destructive"
          )}
        />
        <FieldError fieldName="nume_entitate" errors={fieldErrors} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="cui" className="inline-flex items-center gap-1">
            Codul unic de identificare (CUI){" "}
            <span className="text-destructive ml-1">*</span>
            <FieldTooltip content={ONBOARDING_TOOLTIPS.cui} />
          </Label>
          <Input
            id="cui"
            name="cui"
            autoComplete="on"
            value={entityData.cui || ""}
            onChange={(e) => onFieldChange("cui", e.target.value)}
            placeholder="ex: 12345678"
            className={cn(
              "text-xs md:text-sm",
              fieldHighlight("cui"),
              fieldErrors["cui"] && "border-destructive"
            )}
          />
          <FieldError fieldName="cui" errors={fieldErrors} />
        </div>
        <div>
          <Label
            htmlFor="nr-inregistrare"
            className="inline-flex items-center gap-1"
          >
            Nr. de înregistrare în registre naționale
            <FieldTooltip content={ONBOARDING_TOOLTIPS.nr_inregistrare} />
          </Label>
          <Input
            id="nr-inregistrare"
            value={entityData.cif || ""}
            onChange={(e) => onFieldChange("cif", e.target.value)}
            placeholder="ex: J40/1234/2020"
            className={cn("text-xs md:text-sm", fieldHighlight("cif"))}
          />
        </div>
      </div>

      {duplicateCuiMatch && (
        <div className="flex items-center gap-2 rounded-lg border border-ds-yellow-300 bg-ds-amber-50 p-3">
          <AlertTriangle className="w-4 h-4 text-ds-yellow-600 shrink-0" />
          <span className="text-sm text-ds-yellow-800">
            {duplicateCuiMatch.isArchived
              ? "Această companie este arhivată. Dezarhiveaz-o din Companiile mele pentru a continua."
              : `Există deja o companie cu CUI-ul ${
                  duplicateCuiMatch.session.entity_data?.cui
                } înregistrată în contul tău (${
                  duplicateCuiMatch.session.entity_data?.nume_entitate ||
                  "Entitate necunoscută"
                }). Te rugăm să selectezi un alt CUI.`}
          </span>
        </div>
      )}

      <div>
        <Label htmlFor="adresa" className="inline-flex items-center gap-1">
          Adresa sediu social <span className="text-destructive ml-1">*</span>
          <FieldTooltip content={ONBOARDING_TOOLTIPS.adresa} />
        </Label>
        <Input
          id="adresa"
          value={entityData.adresa || ""}
          onChange={(e) => onFieldChange("adresa", e.target.value)}
          placeholder="Adresa sediului social"
          className={cn(
            "text-xs md:text-sm",
            fieldHighlight("adresa"),
            fieldErrors["adresa"] && "border-destructive"
          )}
        />
        <FieldError fieldName="adresa" errors={fieldErrors} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="telefon">
            Telefon <span className="text-destructive ml-1">*</span>
          </Label>
          <Input
            id="telefon"
            value={entityData.telefon || ""}
            onChange={(e) => onFieldChange("telefon", e.target.value)}
            placeholder="Numărul de telefon"
            className={cn(
              "text-xs md:text-sm",
              fieldHighlight("telefon"),
              fieldErrors["telefon"] && "border-destructive"
            )}
          />
          <FieldError fieldName="telefon" errors={fieldErrors} />
        </div>
        <div>
          <Label htmlFor="email" className="inline-flex items-center gap-1">
            Email <span className="text-destructive ml-1">*</span>
            <FieldTooltip content={ONBOARDING_TOOLTIPS.email} />
          </Label>
          <Input
            id="email"
            type="email"
            value={entityData.email || ""}
            onChange={(e) => onFieldChange("email", e.target.value)}
            placeholder="Email-ul companiei"
            className={cn(
              "text-xs md:text-sm",
              fieldErrors["email"] && "border-destructive"
            )}
          />
          <FieldError fieldName="email" errors={fieldErrors} />
        </div>
      </div>

      <div>
        <Label htmlFor="website">Website</Label>
        <Input
          id="website"
          value={entityData.website || ""}
          onChange={(e) => onFieldChange("website", e.target.value)}
          placeholder="https://..."
          className="text-xs md:text-sm"
        />
      </div>

      <div>
        <Label htmlFor="caen-cod" className="inline-flex items-center gap-1">
          CAEN Principal <span className="text-destructive ml-1">*</span>
          <FieldTooltip content={ONBOARDING_TOOLTIPS.caen_principal} />
        </Label>
        <div className="flex gap-2">
          <Input
            id="caen-cod"
            value={entityData.caen_principal?.cod || ""}
            onChange={(e) =>
              onFieldChange("caen_principal", {
                ...entityData.caen_principal,
                cod: e.target.value,
              })
            }
            placeholder="Cod"
            className={cn(
              "text-xs md:text-sm w-24",
              fieldHighlight("caen_principal"),
              fieldErrors["caen_principal"] && "border-destructive"
            )}
          />
          <Input
            value={entityData.caen_principal?.denumire || ""}
            onChange={(e) =>
              onFieldChange("caen_principal", {
                ...entityData.caen_principal,
                denumire: e.target.value,
              })
            }
            placeholder="Denumire CAEN"
            className="text-xs md:text-sm flex-1"
          />
        </div>
        <FieldError fieldName="caen_principal" errors={fieldErrors} />
      </div>

      <div>
        <Label className="inline-flex items-center gap-1">
          CAEN Secundar
          <FieldTooltip content={ONBOARDING_TOOLTIPS.caen_secundar} />
        </Label>
        <BadgeListInput
          items={entityData.caen_secundar ?? []}
          onItemsChange={(items) => onFieldChange("caen_secundar", items)}
          placeholder="Cod CAEN secundar"
          className="mt-1"
        />
      </div>
    </div>
  );
}

export default CompanyIdentificationSection;
